import type { IntlShape } from "@formatjs/intl";
import { Alert, DatePicker, Flex, Form, Modal, Select, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserDisplayName } from "../../common/utils/userDisplayName";
import {
  assignmentScheduleActions,
  type ScheduleTechniciansPayload,
} from "../../features/assignment-schedule/assignmentScheduleSlice";
import type { TechnicianAssignmentSchedule } from "../generated-axios-client";
import type { RootState } from "../store";
import { formatBookingRange } from "../../common/utils/formatBookingRange";

const API_DATE_FORMAT = "YYYY-MM-DD";

export type WorkOrderToBook = {
  id: string;
  ticketNumber: number | null | undefined;
};

type FormValues = {
  workOrderIds?: string[];
  technicianIds: number[];
  period: [Dayjs, Dayjs] | null;
};

type Props = {
  open: boolean;
  intl: IntlShape;
  // fixed work orders (WO page, project selection); without them the form offers a
  // work order picker, as on the planning board
  workOrders?: WorkOrderToBook[];
  initialTechnicianIds?: number[];
  initialStartDate?: string;
  source: ScheduleTechniciansPayload["source"];
  onClose: () => void;
};

type TechnicianBookings = {
  technicianName: string;
  bookings: TechnicianAssignmentSchedule[];
};

const groupByTechnician = (
  bookings: TechnicianAssignmentSchedule[],
  technicianNames: Record<number, string>
): TechnicianBookings[] => {
  const byTechnician = new Map<number, TechnicianAssignmentSchedule[]>();
  for (const booking of bookings) {
    byTechnician.set(booking.technician, [
      ...(byTechnician.get(booking.technician) ?? []),
      booking,
    ]);
  }
  return [...byTechnician.entries()].map(([technicianId, technicianBookings]) => ({
    technicianName: technicianNames[technicianId] ?? `#${technicianId}`,
    bookings: technicianBookings,
  }));
};

export const ScheduleTechniciansModal = ({
  open,
  intl,
  workOrders,
  initialTechnicianIds,
  initialStartDate,
  source,
  onClose,
}: Props) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm<FormValues>();
  const technicianIds = Form.useWatch("technicianIds", form) ?? [];
  const period = Form.useWatch("period", form);
  const pickedWorkOrderIds = Form.useWatch("workOrderIds", form);
  const choosesWorkOrders = workOrders === undefined;
  const bookableWorkOrders = useSelector(
    (state: RootState) => state.assignmentSchedule.bookableWorkOrders
  );
  const workOrderOptions = useMemo(
    () =>
      (bookableWorkOrders ?? []).map((workOrder) => ({
        value: workOrder.id,
        label: [
          `#${workOrder.ticket_number ?? "—"}`,
          workOrder.name,
          workOrder.project_name,
          workOrder.location_name,
        ]
          .filter(Boolean)
          .join(" · "),
      })),
    [bookableWorkOrders]
  );

  const technicians = useSelector(
    (state: RootState) => state.assignmentSchedule.technicians
  );
  const otherBookings = useSelector(
    (state: RootState) => state.assignmentSchedule.otherBookings
  );
  const saveStatus = useSelector(
    (state: RootState) => state.assignmentSchedule.saveStatus
  );

  const technicianNames = useMemo(
    () =>
      Object.fromEntries(
        (technicians ?? []).map((technician) => [
          technician.id,
          getUserDisplayName(technician),
        ])
      ) as Record<number, string>,
    [technicians]
  );
  const technicianOptions = useMemo(
    () =>
      (technicians ?? [])
        .map((technician) => ({
          value: technician.id,
          label: getUserDisplayName(technician),
        }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [technicians]
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        technicianIds: initialTechnicianIds ?? [],
        period: initialStartDate
          ? [dayjs(initialStartDate), dayjs(initialStartDate)]
          : null,
      });
      dispatch(assignmentScheduleActions.setSaveStatus("idle"));
      dispatch(assignmentScheduleActions.clearOtherBookings());
    }
  }, [open, form, dispatch, initialTechnicianIds, initialStartDate]);

  useEffect(() => {
    if (open && saveStatus === "succeeded") {
      dispatch(assignmentScheduleActions.setSaveStatus("idle"));
      onClose();
    }
  }, [open, saveStatus, dispatch, onClose]);

  const startDate = period?.[0]?.format(API_DATE_FORMAT);
  const endDate = period?.[1]?.format(API_DATE_FORMAT);
  const technicianKey = technicianIds.join(",");
  useEffect(() => {
    if (!open) return;
    if (!startDate || !endDate || technicianKey === "") {
      dispatch(assignmentScheduleActions.clearOtherBookings());
      return;
    }
    dispatch(
      assignmentScheduleActions.fetchOtherBookings({
        technicianIds: technicianKey.split(",").map(Number),
        startDate,
        endDate,
      })
    );
  }, [open, technicianKey, startDate, endDate, dispatch]);

  const selectedWorkOrderIds = useMemo(
    () =>
      workOrders
        ? workOrders.map((workOrder) => workOrder.id)
        : (pickedWorkOrderIds ?? []),
    [workOrders, pickedWorkOrderIds]
  );
  const bookedWorkOrderIds = useMemo(
    () => new Set(selectedWorkOrderIds),
    [selectedWorkOrderIds]
  );
  // The server rejects a second overlapping booking on the same work order, so those
  // block saving; bookings on other work orders are only shown as a heads-up.
  const sameWorkOrderBookings = groupByTechnician(
    otherBookings.filter((booking) => bookedWorkOrderIds.has(booking.ticket)),
    technicianNames
  );
  const otherWorkOrderBookings = groupByTechnician(
    otherBookings.filter((booking) => !bookedWorkOrderIds.has(booking.ticket)),
    technicianNames
  );

  const handleSave = async () => {
    const values = await form.validateFields();
    if (!values.period) return;
    dispatch(
      assignmentScheduleActions.scheduleTechnicians({
        technicianIds: values.technicianIds,
        ticketIds: workOrders
          ? workOrders.map((workOrder) => workOrder.id)
          : (values.workOrderIds ?? []),
        startDate: values.period[0].format(API_DATE_FORMAT),
        endDate: values.period[1].format(API_DATE_FORMAT),
        source,
      })
    );
  };

  const describeBookings = (bookings: TechnicianAssignmentSchedule[]) =>
    bookings
      .map(
        (booking) =>
          `#${booking.ticket_number ?? "—"} (${formatBookingRange(
            intl,
            booking.start_date,
            booking.end_date
          )})`
      )
      .join(", ");

  return (
    <Modal
      open={open}
      title={intl.formatMessage({ id: "schedule_modal_title" })}
      okText={intl.formatMessage({ id: "schedule_modal_save" })}
      cancelText={intl.formatMessage({ id: "cancel" })}
      onOk={handleSave}
      onCancel={onClose}
      okButtonProps={{
        loading: saveStatus === "loading",
        disabled: sameWorkOrderBookings.length > 0,
      }}
      destroyOnHidden
    >
      <Flex vertical gap="middle">
        {workOrders && (
          <Flex vertical gap="small">
            <Typography.Text type="secondary">
              {intl.formatMessage(
                { id: "schedule_modal_work_orders" },
                { count: workOrders.length }
              )}
            </Typography.Text>
            <Flex gap="small" wrap>
              {workOrders.map((workOrder) => (
                <Tag key={workOrder.id}>#{workOrder.ticketNumber ?? "—"}</Tag>
              ))}
            </Flex>
          </Flex>
        )}
        <Form form={form} layout="vertical" requiredMark={false}>
          {choosesWorkOrders && (
            <Form.Item
              name="workOrderIds"
              label={intl.formatMessage({ id: "schedule_modal_pick_work_orders" })}
              rules={[
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: intl.formatMessage({
                    id: "schedule_modal_pick_work_orders_required",
                  }),
                },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                loading={bookableWorkOrders === null}
                options={workOrderOptions}
                placeholder={intl.formatMessage({
                  id: "schedule_modal_pick_work_orders_placeholder",
                })}
              />
            </Form.Item>
          )}
          <Form.Item
            name="technicianIds"
            label={intl.formatMessage({ id: "schedule_modal_technicians" })}
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: intl.formatMessage({
                  id: "schedule_modal_technicians_required",
                }),
              },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={technicians === null}
              options={technicianOptions}
              placeholder={intl.formatMessage({
                id: "schedule_modal_technicians_placeholder",
              })}
            />
          </Form.Item>
          <Form.Item
            name="period"
            label={intl.formatMessage({ id: "schedule_modal_period" })}
            extra={intl.formatMessage({ id: "schedule_modal_period_hint" })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "schedule_modal_period_required" }),
              },
            ]}
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              disabledDate={(date) => date.isBefore(dayjs(), "day")}
            />
          </Form.Item>
        </Form>
        {sameWorkOrderBookings.length > 0 && (
          <Alert
            type="error"
            showIcon
            title={intl.formatMessage({ id: "schedule_modal_same_wo_title" })}
            description={
              <Flex vertical>
                {sameWorkOrderBookings.map(({ technicianName, bookings }) => (
                  <Typography.Text key={technicianName}>
                    {technicianName}: {describeBookings(bookings)}
                  </Typography.Text>
                ))}
              </Flex>
            }
          />
        )}
        {otherWorkOrderBookings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            title={intl.formatMessage({ id: "schedule_modal_other_wo_title" })}
            description={
              <Flex vertical>
                {otherWorkOrderBookings.map(({ technicianName, bookings }) => (
                  <Typography.Text key={technicianName}>
                    {technicianName}: {describeBookings(bookings)}
                  </Typography.Text>
                ))}
              </Flex>
            }
          />
        )}
      </Flex>
    </Modal>
  );
};
